import { useNavigate } from 'react-router-dom';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { showSuccess, showError } from '@/lib/swal';
import { 
  CreateUnitForm, 
  CreateUnitFormTheme 
} from '@masonic-tools/shared-ui';
import {
  checkUnitDuplicate,
  createUnit,
  requestUnitMembership,
  reportUnauthorizedUnit,
} from '@masonic-tools/shared-auth';
import Swal from 'sweetalert2';

const treasurerUnitTheme: CreateUnitFormTheme = {
  appName: 'Treasurer',
  primaryColor: '#10b981',
  title: 'Create Your Unit',
  description: 'Set up your unit to begin managing finances and records.',
  unitNameLabel: 'Unit Name',
  unitNamePlaceholder: 'St. John\'s Unit',
  unitNumberLabel: 'Unit Number',
  unitNumberPlaceholder: 'No. 123',
  submitButtonText: 'Create Unit',
  submittingButtonText: 'Creating...',
  dashboardPath: '/dashboard',
  warningBoxClassName: 'rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm',
};

export default function CreateUnitPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleRequestMembership = async (existingUnitId: string, existingUnitName: string, attemptedName: string, attemptedNumber: string) => {
    if (!currentUser) return;

    try {
      await requestUnitMembership(
        db,
        existingUnitId,
        existingUnitName,
        currentUser.uid,
        currentUser.email!,
        attemptedName,
        attemptedNumber
      );

      showSuccess('Membership request sent successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating membership request:', error);
      showError('Failed to send membership request. Please try again.');
    }
  };

  const handleReportUnauthorized = async (existingUnitId: string, existingUnitName: string, attemptedName: string, attemptedNumber: string) => {
    if (!currentUser) return;

    const { value: reason } = await Swal.fire({
      title: 'Report Unauthorized Unit Creation',
      html: `
        <p class="mb-4">You're reporting that "${existingUnitName}" was created without proper authorization.</p>
        <p class="mb-2">Please provide details:</p>
      `,
      input: 'textarea',
      inputPlaceholder: 'Enter your reason for reporting...',
      showCancelButton: true,
      confirmButtonText: 'Submit Report',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || value.trim().length < 10) {
          return 'Please provide at least 10 characters explaining your report';
        }
        return null;
      },
    });

    if (reason) {
      try {
        await reportUnauthorizedUnit(
          db,
          existingUnitId,
          existingUnitName,
          currentUser.uid,
          currentUser.email!,
          reason,
          attemptedName,
          attemptedNumber
        );

        showSuccess('Report submitted successfully!');
        navigate('/dashboard');
      } catch (error) {
        console.error('Error submitting report:', error);
        showError('Failed to submit report. Please try again.');
      }
    }
  };

  const handleSubmit = async (unitName: string, unitNumber: string) => {
    if (!currentUser) {
      showError('You must be logged in to create a unit');
      return;
    }

    try {
      // Check for duplicates
      const duplicateCheck = await checkUnitDuplicate(db, unitName, unitNumber);

      if (duplicateCheck.exists && duplicateCheck.unit && duplicateCheck.duplicateType) {
        const existingUnit = duplicateCheck.unit;
        const statusText = existingUnit.status === 'pending' ? ' (under review)' : '';
        
        const result = await Swal.fire({
          title: 'Unit Already Exists',
          html: `
            <div class="text-left">
              <p class="mb-4">A unit with the same ${duplicateCheck.duplicateType === 'name' ? 'name' : 'number'} already exists${statusText}:</p>
              <div class="bg-emerald-50 p-3 rounded mb-4 border border-emerald-200">
                <p class="font-semibold text-emerald-900">${existingUnit.name}</p>
                <p class="text-sm text-emerald-700">${existingUnit.unitNumber}</p>
              </div>
              <p class="mb-3">Would you like to:</p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Request Membership',
          denyButtonText: 'Report Unauthorized',
          cancelButtonText: 'Go Back',
          confirmButtonColor: '#10b981',
          denyButtonColor: '#dc2626',
          cancelButtonColor: '#6c757d',
        });

        if (result.isConfirmed) {
          await handleRequestMembership(existingUnit.id, existingUnit.name, unitName, unitNumber);
        } else if (result.isDenied) {
          await handleReportUnauthorized(existingUnit.id, existingUnit.name, unitName, unitNumber);
        }
        return;
      }

      // Create the unit
      await createUnit(db, {
        name: unitName,
        unitNumber: unitNumber,
        userId: currentUser.uid,
        userEmail: currentUser.email!,
        termsAcceptedAt: new Date(),
      });

      await Swal.fire({
        title: 'Unit Submitted for Review',
        html: `
          <div class="text-left">
            <p class="mb-4">Your unit has been submitted for review.</p>
            <div class="bg-emerald-50 p-4 rounded-lg mb-4 border border-emerald-200">
              <p class="font-semibold text-emerald-900">${unitName}</p>
              <p class="text-sm text-emerald-700">${unitNumber}</p>
            </div>
            <p class="mb-3">Next steps:</p>
            <ul class="list-disc pl-5 space-y-1 text-sm">
              <li>Administrators will review your application</li>
              <li>You'll receive email notification when reviewed</li>
              <li>Check your dashboard for status updates</li>
            </ul>
            <p class="mt-4 text-sm text-gray-600">Usually takes 24-48 hours.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Go to Dashboard',
        confirmButtonColor: '#10b981',
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating unit:', error);
      showError('Failed to create unit. Please try again.');
      throw error;
    }
  };

  return (
    <CreateUnitForm
      theme={treasurerUnitTheme}
      onSubmit={handleSubmit}
      currentUser={currentUser}
    />
  );
}

